// ── label / tone tables (single source for every v2 surface) ─────────────────
export const BAND_TONE = {
    clear: 'success',
    minor: 'neutral',
    review: 'warning',
    high: 'danger',
    unobserved: 'neutral',
};
/** Used only when the backend sent no `band_label`. */
export const BAND_FALLBACK_LABEL = {
    clear: 'Clear',
    minor: 'Minor observations',
    review: 'Needs review',
    high: 'Needs close review',
    unobserved: 'Not observed',
};
export const STRENGTH_TONE = {
    strong: 'danger',
    moderate: 'warning',
    weak: 'neutral',
};
export const CONFIDENCE_TONE = {
    high: 'success',
    medium: 'neutral',
    low: 'warning',
};
/** The gaze zones the engine emits, in legend order. */
export const ZONE_ORDER = [
    'on_screen',
    'below',
    'left',
    'right',
    'up',
    'away',
    'eyes_closed',
    'no_face',
    'unknown',
];
export const ZONE_LABEL = {
    on_screen: 'On screen',
    below: 'Below screen',
    left: 'Left',
    right: 'Right',
    up: 'Up',
    away: 'Away',
    eyes_closed: 'Eyes closed',
    no_face: 'No face',
    unknown: 'Unknown',
};
export const zoneLabel = (z) => (z && ZONE_LABEL[z]) || (z ? z.replace(/_/g, ' ') : '—');
// ── helpers ──────────────────────────────────────────────────────────────────
const asArray = (v) => (Array.isArray(v) ? v : []);
const num = (v) => v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
const str = (v) => (v === null || v === undefined ? '' : String(v));
const strList = (v) => asArray(v).map((x) => str(x)).filter(Boolean);
const BANDS = ['clear', 'minor', 'review', 'high'];
const STRENGTHS = ['weak', 'moderate', 'strong'];
const CONFIDENCES = ['high', 'medium', 'low'];
/** The contract's thresholds, used only when the band itself is missing. */
export const bandFromScore = (score) => score >= 95 ? 'clear' : score >= 90 ? 'minor' : score >= 85 ? 'review' : 'high';
const mapSpans = (v) => asArray(v)
    .map((s) => ({ from: num(s === null || s === void 0 ? void 0 : s.from), to: num(s === null || s === void 0 ? void 0 : s.to) }))
    .filter((s) => s.from != null && s.to != null && s.to >= s.from)
    .sort((a, b) => a.from - b.from);
const mapUnobserved = (v) => asArray(v)
    .map((s) => ({ from: num(s === null || s === void 0 ? void 0 : s.from), to: num(s === null || s === void 0 ? void 0 : s.to), reason: str(s === null || s === void 0 ? void 0 : s.reason) }))
    .filter((s) => s.from != null && s.to != null && s.to >= s.from)
    .sort((a, b) => a.from - b.from);
const numRecord = (v) => {
    const out = {};
    if (v && typeof v === 'object') {
        Object.entries(v).forEach(([k, x]) => {
            const n = num(x);
            if (n != null)
                out[k] = n;
        });
    }
    return out;
};
const strRecord = (v) => {
    const out = {};
    if (v && typeof v === 'object') {
        Object.entries(v).forEach(([k, x]) => {
            if (x !== null && x !== undefined && x !== '')
                out[k] = String(x);
        });
    }
    return out;
};
/** True when this raw report carries a usable v2 block. The ONE switch. */
export const isProctoringV2Ready = (report) => {
    var _a;
    if (!report || Number(report.proctoring_version) !== 2)
        return false;
    const block = report.proctoring_v2;
    if (!block || typeof block !== 'object' || block.status !== 'ready')
        return false;
    return num((_a = block.integrity) === null || _a === void 0 ? void 0 : _a.score) != null;
};
/**
 * Pair each `per_answer` row with the adapted transcript entry it describes.
 *
 * The contract keys rows by transcript id. The interview serializer does not
 * send a transcript id today, so the id match only works once it does; until
 * then the row falls back to the FIRST candidate turn carrying the same
 * `question_id` that no other row has claimed. Both raw and adapted arrays come
 * from the same `iv.transcript`, so they share indices.
 */
const matchPerAnswer = (perAnswer, rawTranscript, transcript) => {
    const out = {};
    if (!transcript.length)
        return out;
    const taken = new Set();
    const rawIdOf = (m) => { var _a, _b; return str((_b = (_a = m === null || m === void 0 ? void 0 : m.id) !== null && _a !== void 0 ? _a : m === null || m === void 0 ? void 0 : m._id) !== null && _b !== void 0 ? _b : ''); };
    perAnswer.forEach((row) => {
        let idx = row.key ? rawTranscript.findIndex((m) => rawIdOf(m) === row.key) : -1;
        if (idx < 0 && row.questionId) {
            idx = rawTranscript.findIndex((m, i) => !taken.has(i) &&
                (m === null || m === void 0 ? void 0 : m.sender) !== 'interviewer' &&
                str(m === null || m === void 0 ? void 0 : m.question_id) === row.questionId);
        }
        if (idx < 0 || taken.has(idx) || !transcript[idx])
            return;
        taken.add(idx);
        out[transcript[idx].id] = row;
    });
    return out;
};
// ── main builder ─────────────────────────────────────────────────────────────
/**
 * The v2 view model, or null when the report must render as v1.
 *
 * `transcript` is the v1 adapter's output, passed in so the per-answer chip can
 * address a row by the same id the transcript viewer renders.
 */
export function buildProctoringV2VM(interview, transcript = []) {
    const iv = interview || {};
    const report = iv.final_report || null;
    if (!isProctoringV2Ready(report))
        return null;
    const b = report.proctoring_v2;
    const integ = b.integrity || {};
    const score = Math.max(0, Math.min(100, Math.round(num(integ.score))));
    const band = BANDS.includes(integ.band) ? integ.band : bandFromScore(score);
    const confidence = CONFIDENCES.includes(integ.confidence) ? integ.confidence : 'low';
    const cov = integ.coverage || {};
    const narrativeRaw = b.narrative && typeof b.narrative === 'object' ? b.narrative : null;
    const perFinding = strRecord(narrativeRaw === null || narrativeRaw === void 0 ? void 0 : narrativeRaw.per_finding);
    const findings = asArray(b.findings)
        .map((f) => {
        const id = str(f === null || f === void 0 ? void 0 : f.id);
        return {
            id,
            type: str(f === null || f === void 0 ? void 0 : f.type) || 'F?',
            category: str(f === null || f === void 0 ? void 0 : f.category),
            title: str(f === null || f === void 0 ? void 0 : f.title),
            strength: (STRENGTHS.includes(f === null || f === void 0 ? void 0 : f.strength) ? f.strength : 'weak'),
            timeSpans: mapSpans(f === null || f === void 0 ? void 0 : f.time_spans),
            answers: strList(f === null || f === void 0 ? void 0 : f.answers),
            numbers: str(f === null || f === void 0 ? void 0 : f.numbers),
            whatItLooksLike: str(f === null || f === void 0 ? void 0 : f.what_it_looks_like),
            caveats: str(f === null || f === void 0 ? void 0 : f.caveats),
            evidenceIds: strList(f === null || f === void 0 ? void 0 : f.evidence_ids),
            keyframes: strList(f === null || f === void 0 ? void 0 : f.keyframes),
            parts: strList(f === null || f === void 0 ? void 0 : f.parts),
            explanation: perFinding[id] || null,
        };
    })
        // A finding with no id cannot be linked from the score or the lane, and one
        // with no title renders as an empty card that reads as something hidden.
        .filter((f) => f.id && f.title);
    const contributions = asArray(integ.contributions)
        .map((c) => {
        var _a;
        return ({
            category: str(c === null || c === void 0 ? void 0 : c.category),
            label: str(c === null || c === void 0 ? void 0 : c.label) || str(c === null || c === void 0 ? void 0 : c.category).replace(/_/g, ' '),
            points: (_a = num(c === null || c === void 0 ? void 0 : c.points)) !== null && _a !== void 0 ? _a : 0,
            cap: num(c === null || c === void 0 ? void 0 : c.cap),
            findingIds: strList(c === null || c === void 0 ? void 0 : c.finding_ids),
        });
    })
        .filter((c) => c.label)
        .sort((a, b2) => b2.points - a.points);
    const perAnswer = asArray(b.per_answer).map((r) => {
        const observed = (r === null || r === void 0 ? void 0 : r.observed) !== false;
        const ind = r === null || r === void 0 ? void 0 : r.indicator;
        return {
            key: str(r === null || r === void 0 ? void 0 : r.key),
            questionId: (r === null || r === void 0 ? void 0 : r.question_id) != null && r.question_id !== '' ? String(r.question_id) : null,
            question: str(r === null || r === void 0 ? void 0 : r.question),
            start: num(r === null || r === void 0 ? void 0 : r.start),
            end: num(r === null || r === void 0 ? void 0 : r.end),
            observed,
            observedRatio: num(r === null || r === void 0 ? void 0 : r.observed_ratio),
            dominantZone: (r === null || r === void 0 ? void 0 : r.dominant_zone) ? String(r.dominant_zone) : null,
            offScreenRatio: num(r === null || r === void 0 ? void 0 : r.off_screen_ratio),
            latencySec: num(r === null || r === void 0 ? void 0 : r.latency_sec),
            findingIds: strList(r === null || r === void 0 ? void 0 : r.finding_ids),
            indicator: (ind === 'unobserved' || BANDS.includes(ind)
                ? ind
                : observed
                    ? 'clear'
                    : 'unobserved'),
        };
    });
    const narrative = narrativeRaw
        ? {
            status: (['ready', 'failed', 'skipped'].includes(narrativeRaw.status)
                ? narrativeRaw.status
                : 'skipped'),
            summary: str(narrativeRaw.summary),
            perFinding,
            checklist: asArray(narrativeRaw.checklist)
                .map((c) => ({
                t: num(c === null || c === void 0 ? void 0 : c.t),
                findingId: (c === null || c === void 0 ? void 0 : c.finding_id) ? String(c.finding_id) : null,
                whatToWatch: str(c === null || c === void 0 ? void 0 : c.what_to_watch),
            }))
                .filter((c) => c.t != null && !!c.whatToWatch)
                .sort((a, b2) => a.t - b2.t),
        }
        : null;
    const keyframes = asArray(b.keyframes)
        .map((k) => {
        var _a;
        return ({
            id: str(k === null || k === void 0 ? void 0 : k.id),
            findingId: (k === null || k === void 0 ? void 0 : k.finding_id) ? String(k.finding_id) : null,
            t: (_a = num(k === null || k === void 0 ? void 0 : k.t)) !== null && _a !== void 0 ? _a : 0,
            key: str(k === null || k === void 0 ? void 0 : k.key),
            description: str(k === null || k === void 0 ? void 0 : k.description),
        });
    })
        .filter((k) => k.id)
        .sort((a, b2) => a.t - b2.t);
    const cal = b.calibration && typeof b.calibration === 'object' ? b.calibration : null;
    const calibration = cal
        ? {
            method: str(cal.method),
            quality: num(cal.quality),
            referenceSec: num(cal.reference_sec),
            center: cal.center && num(cal.center.yaw) != null && num(cal.center.pitch) != null
                ? { yaw: Number(cal.center.yaw), pitch: Number(cal.center.pitch) }
                : null,
        }
        : null;
    const prov = b.provenance && typeof b.provenance === 'object' ? b.provenance : null;
    const provenance = prov
        ? {
            engine: str(prov.engine),
            formulaVersion: str(prov.formula_version),
            sources: numRecord(prov.sources),
            fps: numRecord(prov.fps),
            models: strRecord(prov.models),
            durationMs: num(prov.duration_ms),
            browser: prov.browser && typeof prov.browser === 'object'
                ? {
                    status: str(prov.browser.status),
                    checked: num(prov.browser.checked),
                    tiers: Array.isArray(prov.browser.tiers) ? prov.browser.tiers.map(String) : [],
                    delegates: Array.isArray(prov.browser.delegates) ? prov.browser.delegates.map(String) : [],
                }
                : null,
        }
        : null;
    return {
        slug: iv.slug ? String(iv.slug) : null,
        formulaVersion: str(b.formula_version),
        generatedAt: b.generated_at ? String(b.generated_at) : null,
        score,
        band,
        bandLabel: str(integ.band_label) || BAND_FALLBACK_LABEL[band],
        bandTone: BAND_TONE[band],
        confidence,
        confidenceReasons: strList(integ.confidence_reasons),
        coverage: {
            totalSec: num(cov.total_sec),
            observedSec: num(cov.observed_sec),
            observedRatio: num(cov.observed_ratio),
            answersTotal: num(cov.answers_total),
            answersObserved: num(cov.answers_observed),
            unobserved: mapUnobserved(cov.unobserved),
        },
        contributions,
        findings,
        perAnswer,
        perAnswerByTranscriptId: matchPerAnswer(perAnswer, asArray(iv.transcript), transcript),
        narrative,
        keyframes,
        calibration,
        provenance,
        tracksAvailable: b.tracks_available !== false,
    };
}
/** Findings strongest-first, then earliest — the order every list uses. */
const STRENGTH_RANK = { strong: 2, moderate: 1, weak: 0 };
export const rankFindings = (findings) => [...findings].sort((a, b) => {
    var _a, _b, _c, _d;
    return STRENGTH_RANK[b.strength] - STRENGTH_RANK[a.strength] ||
        ((_b = (_a = a.timeSpans[0]) === null || _a === void 0 ? void 0 : _a.from) !== null && _b !== void 0 ? _b : Infinity) - ((_d = (_c = b.timeSpans[0]) === null || _c === void 0 ? void 0 : _c.from) !== null && _d !== void 0 ? _d : Infinity);
});
export const strengthRank = (s) => STRENGTH_RANK[s];
const mapPoint = (p) => {
    if (!Array.isArray(p) || p.length < 2)
        return null;
    const x = num(p[0]);
    const y = num(p[1]);
    return x == null || y == null ? null : [x, y];
};
const mapPoints = (v) => asArray(v).map(mapPoint).filter((p) => !!p);
const WHO = ['candidate', 'interviewer', 'other'];
/** Normalise the tracks payload. Everything is sorted by time here, because the
 *  lanes and the overlay both binary-search it. */
export function mapProctoringV2Tracks(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    return {
        slug: str(raw.slug),
        durationSec: num(raw.duration_sec),
        offsets: numRecord(raw.offsets),
        zones: asArray(raw.zones)
            .map((z) => ({ from: num(z === null || z === void 0 ? void 0 : z.from), to: num(z === null || z === void 0 ? void 0 : z.to), zone: str(z === null || z === void 0 ? void 0 : z.zone) || 'unknown' }))
            .filter((z) => z.from != null && z.to != null && z.to > z.from)
            .sort((a, b) => a.from - b.from),
        speech: asArray(raw.speech)
            .map((s) => ({
            from: num(s === null || s === void 0 ? void 0 : s.from),
            to: num(s === null || s === void 0 ? void 0 : s.to),
            who: (WHO.includes(s === null || s === void 0 ? void 0 : s.who) ? s.who : 'other'),
        }))
            .filter((s) => s.from != null && s.to != null && s.to > s.from)
            .sort((a, b) => a.from - b.from),
        browser: asArray(raw.browser)
            .map((e) => { var _a; return ({ t: num(e === null || e === void 0 ? void 0 : e.t), kind: str(e === null || e === void 0 ? void 0 : e.kind) || 'event', duration: (_a = num(e === null || e === void 0 ? void 0 : e.duration)) !== null && _a !== void 0 ? _a : 0 }); })
            .filter((e) => e.t != null)
            .sort((a, b) => a.t - b.t),
        holds: asArray(raw.holds)
            .map((h) => ({ from: num(h === null || h === void 0 ? void 0 : h.from), to: num(h === null || h === void 0 ? void 0 : h.to), kind: str(h === null || h === void 0 ? void 0 : h.kind) }))
            .filter((h) => h.from != null && h.to != null && h.to >= h.from)
            .sort((a, b) => a.from - b.from),
        coding: mapSpans(raw.coding),
        unobserved: mapUnobserved(raw.unobserved),
        findingsLane: asArray(raw.findings_lane)
            .map((f) => ({
            from: num(f === null || f === void 0 ? void 0 : f.from),
            to: num(f === null || f === void 0 ? void 0 : f.to),
            findingId: str(f === null || f === void 0 ? void 0 : f.finding_id),
            type: str(f === null || f === void 0 ? void 0 : f.type),
            strength: (STRENGTHS.includes(f === null || f === void 0 ? void 0 : f.strength) ? f.strength : 'weak'),
        }))
            .filter((f) => f.from != null && f.to != null && f.to >= f.from && !!f.findingId)
            .sort((a, b) => a.from - b.from),
        overlay: asArray(raw.overlay)
            .map((r) => ({
            t: num(r === null || r === void 0 ? void 0 : r.t),
            src: str(r === null || r === void 0 ? void 0 : r.src),
            space: ((r === null || r === void 0 ? void 0 : r.space) === 'webcam' ? 'webcam' : 'frame'),
            face: mapPoints(r === null || r === void 0 ? void 0 : r.face),
            gaze: (r === null || r === void 0 ? void 0 : r.gaze) && num(r.gaze.x) != null && num(r.gaze.y) != null
                ? { x: Number(r.gaze.x), y: Number(r.gaze.y) }
                : null,
            zone: (r === null || r === void 0 ? void 0 : r.zone) ? String(r.zone) : null,
            hands: asArray(r === null || r === void 0 ? void 0 : r.hands).map(mapPoints).filter((h) => h.length > 0),
        }))
            .filter((r) => r.t != null)
            .sort((a, b) => a.t - b.t),
        overlayLandmarks: asArray(raw.overlay_landmarks)
            .map((i) => num(i))
            .filter((i) => i != null),
        // Same shape as v1's `proctoring_layout` rows; sorted for findLayoutAt.
        layout: asArray(raw.layout)
            .filter((r) => r && num(r.t) != null)
            .slice()
            .sort((a, b) => Number(a.t) - Number(b.t)),
        keyframeUrls: strRecord(raw.keyframe_urls),
        headPose: asArray(raw.head_pose)
            .map((p) => ({ t: num(p === null || p === void 0 ? void 0 : p.t), yaw: num(p === null || p === void 0 ? void 0 : p.yaw), pitch: num(p === null || p === void 0 ? void 0 : p.pitch), roll: num(p === null || p === void 0 ? void 0 : p.roll) }))
            .filter((p) => p.t != null && p.yaw != null && p.pitch != null)
            .sort((a, b) => a.t - b.t),
    };
}
